// Global Instructions Rule Applied!
// Shape returned by wf__hypothetical_liquidation, the read of the Statements
// tab's Waterfall position panel (docs/WATERFALL_CARRY_DESIGN.md, phase 4): the
// fund's partners with their booked waterfall state (wf__state) and the
// hypothetical liquidation of their capital accounts at fair market value -
// the accrued carried interest. The backend passes no data for a fund with no
// open waterfall. A legal entity of a consolidated fund gets no figures:
// consolidated_fund names the consolidated fund, partners is empty and totals
// is null.
// Mirrors backend/src/data_manager/data_models/fund_waterfall_position_record.py.
// Ids are plain strings: funds and commitments carry Dataverse GUIDs whose
// version nibble fails zod's RFC-4122 .uuid() check.
import { z } from 'zod'

// what a tier paid (by_tier) or would pay (hypothetical_by_tier) the partner and the GP
export const WaterfallPositionTierSchema = z
  .object({
    tier_sequence: z.number().int(),
    tier_type: z.string(),
    lp: z.number(),
    gp: z.number()
  })
  .strict()

// a partner's part of one pool (a waterfall with pools, phase 6 PR 3)
export const WaterfallPositionPoolSchema = z
  .object({
    pool_id: z.string(),
    pool_code: z.string(),
    contributions: z.number(),
    capital_returned: z.number(),
    remediation_received: z.number(),
    remediation_given: z.number(),
    capital_due: z.number(),
    profit_received: z.number(),
    carry_paid: z.number(),
    unremedied_loss: z.number()
  })
  .strict()

export const WaterfallPositionPartnerSchema = z
  .object({
    fund_commitment_id: z.string(),
    investor_name: z.string().nullish(),
    // the general partner's own commitment: its share is paid as such, it bears no carry
    is_gp_commitment: z.boolean(),
    // the row the carried interest is allocated and paid to - the GP commitment
    // itself in most funds, a row of its own otherwise. An investor: both false
    is_carry_recipient: z.boolean(),
    commitment: z.number(),
    // the ledger's stamped contributions (wf__state); a transferee's sit on
    // the transferor until the ledger read follows the lineage (phase 6)
    contributions: z.number(),
    // an investor: lp, carry_borne, priority; a row outside the investors (the
    // GP commitment, the carry recipient): own_share, carry_received
    distributions: z
      .object({
        total: z.number(),
        lp: z.number().nullish(),
        carry_borne: z.number().nullish(),
        priority: z.number().nullish(),
        own_share: z.number().nullish(),
        carry_received: z.number().nullish()
      })
      .strict(),
    by_tier: z.array(WaterfallPositionTierSchema),
    unreturned_capital: z.number(),
    multiple_reached: z.number().nullish(),
    catch_up_remaining: z.number(),
    carry_paid: z.number(),
    recallable_balance: z.number().nullish(),
    // the commitment's carry terms; not on a row outside the investors
    terms: z
      .object({
        base_rate: z.number().nullish(),
        base_hurdle: z.number().nullish(),
        stepped_rate: z.number().nullish(),
        stepped_hurdle: z.number().nullish(),
        from_primary_pocket: z.boolean().nullish(),
        // the base rate is the fund's default term (no side-letter term on the row)
        from_fund: z.boolean().nullish()
      })
      .strict()
      .nullish(),
    // the hypothetical liquidation
    fmv_capital: z.number(),
    proceeds: z.number(),
    accrued_carry: z.number(),
    fmv_capital_after_carry: z.number(),
    hypothetical_by_tier: z.array(WaterfallPositionTierSchema),
    // only on an investor of a waterfall with pools, oldest pool first
    pools: z.array(WaterfallPositionPoolSchema).nullish()
  })
  .strict()

// over the investors: the GP commitment is not a limited partner
export const WaterfallPositionTotalsSchema = z
  .object({
    partners: z.number().int(),
    contributions: z.number(),
    lp_received: z.number(),
    carry_paid: z.number(),
    gp_own_share: z.number(),
    multiple_reached: z.number().nullish(),
    catch_up_remaining: z.number(),
    unreturned_capital: z.number(),
    fmv_capital: z.number(),
    accrued_carry: z.number(),
    fmv_capital_after_carry: z.number()
  })
  .strict()

export const FundWaterfallPositionSchema = z
  .object({
    fund_id: z.string(),
    as_of: z.string(),
    waterfall_id: z.string().nullish(),
    consolidated_fund: z
      .object({
        fund_groups__id: z.string(),
        fund_groups__name: z.string().nullish()
      })
      .strict()
      .nullish(),
    partners: z.array(WaterfallPositionPartnerSchema),
    totals: WaterfallPositionTotalsSchema.nullish()
  })
  .strict()

export type WaterfallPositionTier = z.infer<typeof WaterfallPositionTierSchema>
export type WaterfallPositionPool = z.infer<typeof WaterfallPositionPoolSchema>
export type WaterfallPositionPartner = z.infer<typeof WaterfallPositionPartnerSchema>
export type WaterfallPositionTotals = z.infer<typeof WaterfallPositionTotalsSchema>
export type FundWaterfallPosition = z.infer<typeof FundWaterfallPositionSchema>

export const validateFundWaterfallPosition = (data: unknown): FundWaterfallPosition => {
  const result = FundWaterfallPositionSchema.safeParse(data)
  if (!result.success) {
    throw new Error(`Fund waterfall position validation failed: ${result.error.message}`)
  }
  return result.data
}
